import { supabase } from '@/integrations/supabase/client';

interface AuditLogEntry {
  entityType: string;
  entityId: string;
  action: 'create' | 'update' | 'delete' | 'bulk_delete';
  oldValue?: Record<string, any> | null;
  newValue?: Record<string, any> | null;
  changedFields?: string[];
  metadata?: Record<string, any>;
  source?: 'ui' | 'api' | 'system';
}

export interface FieldChange {
  field: string;
  oldValue: any;
  newValue: any;
}

export interface AuditLogRecord {
  id: string;
  entity_type: string;
  entity_id: string;
  action: string;
  old_value: Record<string, any> | null;
  new_value: Record<string, any> | null;
  actor_id: string | null;
  created_at: string;
  app_users?: {
    full_name: string;
    email: string;
    avatar_url: string | null;
  } | null;
}

class AuditService {
  /**
   * Get the current user's app_users ID
   */
  private async getActorId(): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    
    const { data: appUser } = await supabase
      .from('app_users')
      .select('id')
      .eq('user_id', user.id)
      .single();
    
    return appUser?.id || null;
  }

  /**
   * Log an audit entry to the activity_logs table
   */
  async log(entry: AuditLogEntry): Promise<void> {
    try {
      const actorId = await this.getActorId();
      
      await supabase.from('activity_logs').insert({
        entity_type: entry.entityType,
        entity_id: entry.entityId,
        action: entry.action,
        old_value: entry.oldValue || null,
        new_value: entry.newValue || null,
        actor_id: actorId,
      });
    } catch (error) {
      console.error('Failed to log audit entry:', error);
    }
  }

  /**
   * Log a create action
   */
  async logCreate(entityType: string, entityId: string, newValue: Record<string, any>): Promise<void> {
    await this.log({
      entityType,
      entityId,
      action: 'create',
      newValue,
    });
  }

  /**
   * Log an update action with field changes
   */
  async logUpdate(
    entityType: string, 
    entityId: string, 
    oldValue: Record<string, any>, 
    newValue: Record<string, any>
  ): Promise<void> {
    await this.log({
      entityType,
      entityId,
      action: 'update',
      oldValue,
      newValue,
      changedFields: this.getChangedFields(oldValue, newValue).map(c => c.field),
    });
  }

  /**
   * Log a delete action
   */
  async logDelete(entityType: string, entityId: string, oldValue: Record<string, any>): Promise<void> {
    await this.log({
      entityType,
      entityId,
      action: 'delete',
      oldValue,
    });
  }

  /**
   * Log a bulk delete action
   */
  async logBulkDelete(entityType: string, entityIds: string[]): Promise<void> {
    for (const entityId of entityIds) {
      await this.log({
        entityType,
        entityId,
        action: 'bulk_delete',
      });
    }
  }

  /**
   * Get the fields that changed between two objects
   */
  getChangedFields(
    oldValue: Record<string, any>, 
    newValue: Record<string, any>, 
    excludeFields: string[] = ['updated_at', 'created_at']
  ): FieldChange[] {
    const changes: FieldChange[] = [];
    const allKeys = new Set([...Object.keys(oldValue), ...Object.keys(newValue)]);
    
    allKeys.forEach(key => {
      if (excludeFields.includes(key)) return;
      
      const oldVal = oldValue[key];
      const newVal = newValue[key];
      
      // Deep comparison using JSON stringify
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          field: key,
          oldValue: oldVal,
          newValue: newVal,
        });
      }
    });
    
    return changes;
  }

  /**
   * Get audit history for a specific entity
   */
  async getEntityHistory(
    entityType: string, 
    entityId: string, 
    limit = 50
  ): Promise<AuditLogRecord[]> {
    const { data, error } = await supabase
      .from('activity_logs')
      .select(`
        *,
        app_users (full_name, email, avatar_url)
      `)
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return (data || []) as AuditLogRecord[];
  }

  /**
   * Get recent audit logs across all entities
   */
  async getRecentLogs(limit = 100): Promise<AuditLogRecord[]> {
    const { data, error } = await supabase
      .from('activity_logs')
      .select(`
        *,
        app_users (full_name, email, avatar_url)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return (data || []) as AuditLogRecord[];
  }

  /**
   * Get audit logs for a specific user
   */
  async getUserActivity(userId: string, limit = 50): Promise<AuditLogRecord[]> {
    const { data, error } = await supabase
      .from('activity_logs')
      .select(`
        *,
        app_users (full_name, email, avatar_url)
      `)
      .eq('actor_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    return (data || []) as AuditLogRecord[];
  }

  /**
   * Format field changes for display
   */
  formatFieldChanges(changes: FieldChange[]): string {
    return changes
      .map(change => `${change.field}: ${this.formatValue(change.oldValue)} → ${this.formatValue(change.newValue)}`)
      .join(', ');
  }

  /**
   * Format a value for display
   */
  private formatValue(value: any): string {
    if (value === null || value === undefined) return '(empty)';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }
}

export const auditService = new AuditService();
